import axios, { AxiosError } from 'axios';
import { SessionRecord, WireBatch, toWire } from 'Grading';
import logger from 'Utils/Logger';
import { reportError } from 'Utils/reportError';
import { getCurrentAuthToken } from 'Utils/Auth';
import { MAX_RESENDS, isDeviceOffline, isPermanentReject, resendDelayMs } from './eventSendPolicy';
import { claimKeepaliveChars, keepaliveCharsAvailable } from './keepaliveBudget';

// The take-records outbox: sends a surface's session record (its realized scores, its takes
// and its stream of key events) to the take-records endpoint, a part at a time.
//
// Every part of a body is idempotent on the server (a session, material or take by its id, a
// segment by `<session id>:<seq>`), so a resend is harmless. The outbox remembers which ended
// takes and which segments the server has accepted and sends only the rest, within the
// server's per-body limits. A failed send follows the event queue's rules
// (eventSendPolicy): a permanent 4xx drops that session (the server refuses a body whole,
// so its later parts are not sent either) and reports it; any other failure is resent with
// backoff, up to MAX_RESENDS. On teardown (page hidden, app
// backgrounded) what is left goes over keepalive, after the two event senders, within what is
// left of the page's keepalive allowance. On sign-out everything not yet accepted is dropped.

const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/`;
const STREAM_PATH = 'take-records/stream';

// The server's limits on one body.
export const MAX_TAKES_PER_BODY = 50;
export const MAX_SEGMENTS_PER_BODY = 50;

export interface AcceptedParts {
  // Ended takes the server has stored (an open take's header is resent with its segments).
  readonly takes: ReadonlySet<string>;
  readonly segments: ReadonlySet<number>;
}

export const NOTHING_ACCEPTED: AcceptedParts = { takes: new Set(), segments: new Set() };

// The next body for a session: segments not yet accepted (oldest first), the takes they
// belong to, then ended takes not yet accepted, and the materials those takes grade against.
// Null when the server holds everything.
export function unsentBody(record: SessionRecord, accepted: AcceptedParts): WireBatch | null {
  const wire = toWire(record);
  const segments = wire.segments
    .filter((segment) => !accepted.segments.has(segment.seq))
    .slice(0, MAX_SEGMENTS_PER_BODY);
  const segmentTakeIds = new Set(segments.map((segment) => segment.take_id));
  const forSegments = wire.takes.filter((take) => segmentTakeIds.has(take.id));
  const endedUnsent = wire.takes.filter(
    (take) => take.end !== null && !accepted.takes.has(take.id) && !segmentTakeIds.has(take.id),
  );
  const takes = [...forSegments, ...endedUnsent].slice(0, MAX_TAKES_PER_BODY);
  if (segments.length === 0 && takes.length === 0) return null;
  const materialIds = new Set(takes.map((take) => take.material_id));
  return {
    session: wire.session,
    materials: wire.materials.filter((material) => materialIds.has(material.id)),
    takes,
    segments,
  };
}

// The parts of a body the server now holds.
export function acceptedAfter(accepted: AcceptedParts, body: WireBatch): AcceptedParts {
  return {
    takes: new Set([
      ...accepted.takes,
      ...body.takes.filter((take) => take.end !== null).map((take) => take.id),
    ]),
    segments: new Set([...accepted.segments, ...body.segments.map((segment) => segment.seq)]),
  };
}

interface OutboxEntry {
  readonly record: SessionRecord;
  readonly accepted: AcceptedParts;
  readonly failedSends: number;
}

const sendingEnabled = (): boolean => process.env.REACT_APP_SEND_EVENTS === 'true';

const statusOf = (err: unknown): number | undefined =>
  err instanceof AxiosError ? err.response?.status : undefined;

/** What the server said was wrong with a refused body (its `errors` or `message`), if anything. */
export const refusalReasons = (body: unknown): string[] => {
  if (body === null || typeof body !== 'object') return [];
  const { errors, message } = body as { errors?: unknown; message?: unknown };
  const listed = Array.isArray(errors) ? errors : Array.isArray(message) ? message : [message];
  return listed.filter((reason): reason is string => typeof reason === 'string').slice(0, 5);
};

/**
 * A refused session, reported with the server's reasons so a missing record can be traced (the
 * session id, surface and take count say which record it was; no key events are reported).
 */
const refusalError = (
  how: string,
  status: number,
  body: unknown,
  record: SessionRecord | undefined,
): Error =>
  new Error(
    `take records ${how} rejected ${JSON.stringify({
      httpStatus: status,
      reasons: refusalReasons(body),
      surface: record?.header.surface,
      takes: record?.takes.length,
    })}`,
  );

export class TakeRecordOutbox {
  private entries = new Map<string, OutboxEntry>();
  // Sessions with a send in flight: one send per session at a time, in order.
  private sending = new Set<string>();
  private resendTimers = new Map<string, ReturnType<typeof setTimeout>>();
  // Sessions the server refused for good: a resend would be refused the same way.
  private refused = new Set<string>();
  // Bumped by discardPending (sign-out): a send that answers afterwards changes nothing.
  private generation = 0;

  // A session's record as it stands now; what the server does not hold yet is sent.
  submit(record: SessionRecord): void {
    if (!sendingEnabled()) return;
    const sessionId = record.header.sessionId;
    if (this.refused.has(sessionId)) return;
    const previous = this.entries.get(sessionId);
    this.entries.set(sessionId, {
      record,
      accepted: previous?.accepted ?? NOTHING_ACCEPTED,
      failedSends: previous?.failedSends ?? 0,
    });
    void this.send(sessionId);
  }

  // A session's record as it stands when the page is going away (its sitting just ended): kept
  // like `submit`, but sent over keepalive, which outlives the page.
  submitOnTeardown(record: SessionRecord): void {
    if (!sendingEnabled()) return;
    const sessionId = record.header.sessionId;
    if (this.refused.has(sessionId)) return;
    const previous = this.entries.get(sessionId);
    this.entries.set(sessionId, {
      record,
      accepted: previous?.accepted ?? NOTHING_ACCEPTED,
      failedSends: previous?.failedSends ?? 0,
    });
    this.flushOnTeardown().catch(() => {});
  }

  // Sends the session's unsent parts, one body after another, until the server holds them all
  // or a send fails.
  private async send(sessionId: string): Promise<void> {
    if (this.sending.has(sessionId)) return;
    this.clearResendTimer(sessionId);
    this.sending.add(sessionId);
    const generation = this.generation;
    try {
      let sent = true;
      let next = this.nextBody(sessionId);
      while (sent && next !== null && generation === this.generation) {
        sent = await this.post(sessionId, next.entry, next.body);
        next = this.nextBody(sessionId);
      }
    } finally {
      if (generation === this.generation) this.sending.delete(sessionId);
    }
  }

  private nextBody(sessionId: string): { entry: OutboxEntry; body: WireBatch } | null {
    const entry = this.entries.get(sessionId);
    const body = entry ? unsentBody(entry.record, entry.accepted) : null;
    return entry && body ? { entry, body } : null;
  }

  // One body: true when the server took it, so the next can follow.
  private async post(sessionId: string, entry: OutboxEntry, body: WireBatch): Promise<boolean> {
    const generation = this.generation;
    try {
      await axios.post(baseUrl + STREAM_PATH, body);
      if (generation !== this.generation) return false;
      this.markAccepted(sessionId, body);
      return true;
    } catch (err) {
      if (generation !== this.generation) return false;
      this.onFailedSend(sessionId, entry, statusOf(err), err);
      return false;
    }
  }

  private markAccepted(sessionId: string, body: WireBatch): void {
    const current = this.entries.get(sessionId);
    if (!current) return;
    this.entries.set(sessionId, {
      ...current,
      accepted: acceptedAfter(current.accepted, body),
      failedSends: 0,
    });
  }

  private onFailedSend(
    sessionId: string,
    entry: OutboxEntry,
    status: number | undefined,
    err: unknown,
  ): void {
    if (status !== undefined && isPermanentReject(status)) {
      this.entries.delete(sessionId);
      this.refused.add(sessionId);
      const body = err instanceof AxiosError ? err.response?.data : undefined;
      reportError(refusalError('POST', status, body, entry.record), 'take-records.post');
      return;
    }
    const failedSends = isDeviceOffline() ? entry.failedSends : entry.failedSends + 1;
    if (failedSends > MAX_RESENDS) {
      this.entries.delete(sessionId);
      reportError(err, 'take-records.post');
      return;
    }
    logger.warn('[take-records] send failed; will resend', { status, failedSends });
    const current = this.entries.get(sessionId);
    if (current) this.entries.set(sessionId, { ...current, failedSends });
    this.resendTimers.set(
      sessionId,
      setTimeout(
        () => {
          this.resendTimers.delete(sessionId);
          void this.send(sessionId);
        },
        resendDelayMs(Math.max(1, failedSends)),
      ),
    );
  }

  private clearResendTimer(sessionId: string): void {
    const timer = this.resendTimers.get(sessionId);
    if (timer !== undefined) clearTimeout(timer);
    this.resendTimers.delete(sessionId);
  }

  // Page hidden, app backgrounded or pagehide: send what is left over keepalive, which
  // survives the page going away, within what is left of the keepalive allowance. A session
  // with a send in flight is left to it.
  async flushOnTeardown(): Promise<void> {
    if (!sendingEnabled() || typeof fetch !== 'function') return;
    const outgoing = [...this.entries.entries()]
      .filter(([sessionId]) => !this.sending.has(sessionId))
      .map(([sessionId, entry]) => ({ sessionId, body: unsentBody(entry.record, entry.accepted) }))
      .filter((item): item is { sessionId: string; body: WireBatch } => item.body !== null)
      .map((item) => ({ ...item, text: JSON.stringify(item.body) }))
      .filter((item) => item.text.length <= keepaliveCharsAvailable())
      .map((item) => {
        this.sending.add(item.sessionId);
        return { ...item, release: claimKeepaliveChars(item.text.length) };
      });
    if (outgoing.length === 0) return;
    let token: string | undefined;
    try {
      token = await getCurrentAuthToken();
    } catch {
      token = undefined;
    }
    const generation = this.generation;
    await Promise.all(
      outgoing.map(async ({ sessionId, body, text, release }) => {
        try {
          const response = await fetch(baseUrl + STREAM_PATH, {
            method: 'POST',
            keepalive: true,
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: text,
          });
          if (generation !== this.generation) return;
          if (response.ok) {
            this.markAccepted(sessionId, body);
          } else if (isPermanentReject(response.status)) {
            const record = this.entries.get(sessionId)?.record;
            this.entries.delete(sessionId);
            this.refused.add(sessionId);
            const body =
              typeof response.json === 'function'
                ? await response.json().catch(() => undefined)
                : undefined;
            reportError(
              refusalError('keepalive POST', response.status, body, record),
              'take-records.post',
            );
          }
        } catch (err) {
          logger.warn('[take-records] keepalive send failed', err);
        } finally {
          release();
          if (generation === this.generation) this.sending.delete(sessionId);
        }
      }),
    );
  }

  // Sign-out: drop every part not yet accepted, so none is sent later under the account that
  // signs in next.
  discardPending(): void {
    this.resendTimers.forEach((timer) => clearTimeout(timer));
    this.resendTimers.clear();
    this.entries.clear();
    this.sending.clear();
    this.generation += 1;
  }
}

// singleton, like the event queue
export const takeRecordOutbox = new TakeRecordOutbox();
